import { useRef, useEffect } from "react";
import { LayoutGrid, Palette, Sparkles, Waves } from "lucide-react";
import { useBlobField } from "./BlobFieldContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";

export function BlobFieldControls() {
    const {
        columns,
        rows,
        cellSize,
        seed,
        pointDensity,
        influenceRadius,
        baseThreshold,
        contourLevels,
        contourStep,
        showGridPoints,
        fillColor,
        strokeColor,
        setColumns,
        setRows,
        setCellSize,
        setSeed,
        setPointDensity,
        setInfluenceRadius,
        setBaseThreshold,
        setContourLevels,
        setContourStep,
        setShowGridPoints,
        setFillColor,
        setStrokeColor,
        canvasW,
        canvasH,
        gridPoints,
        randomizeParameters,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useBlobField();

    const activePointCount = gridPoints.filter((p) => p.weight > 0).length;

    const fillColorTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const strokeColorTimeout = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );

    useEffect(() => {
        return () => {
            if (fillColorTimeout.current)
                clearTimeout(fillColorTimeout.current);
            if (strokeColorTimeout.current)
                clearTimeout(strokeColorTimeout.current);
        };
    }, []);

    const handleFillColorPicker = (value: string) => {
        if (fillColorTimeout.current) clearTimeout(fillColorTimeout.current);
        fillColorTimeout.current = setTimeout(() => setFillColor(value), 150);
    };

    const handleStrokeColorPicker = (value: string) => {
        if (strokeColorTimeout.current)
            clearTimeout(strokeColorTimeout.current);
        strokeColorTimeout.current = setTimeout(
            () => setStrokeColor(value),
            150,
        );
    };

    const handleInt =
        (setter: (v: number) => void, min = 1) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min) return;
            setter(Math.round(n));
        };

    const handleFloat =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    return (
        <div className="panel-controls blob-field-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Blob Field</h2>
                    <ToolDescriptionPopover dialogLabel="Blob Field tool description">
                        <p>
                            Random weights on a grid merge into organic blobs
                            via a Gaussian influence field and iso-contour
                            tracing.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={randomizeParameters}
                    >
                        Randomize parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="blob-field"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <CollapsibleToolGroup title="Grid" icon={LayoutGrid}>
                    <div className="field-row">
                        <label className="field-label">
                            Columns
                            <DebouncedInput
                                type="number"
                                min={2}
                                max={40}
                                step={1}
                                value={columns}
                                onChange={handleInt(setColumns, 2)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Rows
                            <DebouncedInput
                                type="number"
                                min={2}
                                max={40}
                                step={1}
                                value={rows}
                                onChange={handleInt(setRows, 2)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Cell size (px)
                            <DebouncedInput
                                type="number"
                                min={10}
                                max={200}
                                step={5}
                                value={cellSize}
                                onChange={handleInt(setCellSize, 10)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <p className="control-panel-hint">
                        Canvas: {canvasW} × {canvasH} px
                    </p>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Randomisation" icon={Sparkles}>
                    <div className="field-row">
                        <label className="field-label">
                            Seed
                            <DebouncedInput
                                type="text"
                                value={seed}
                                onChange={(v) => setSeed(String(v))}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Point density (0–1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={pointDensity}
                                onChange={handleFloat(setPointDensity, 0, 1)}
                            />
                        </label>
                    </div>
                    <p className="control-panel-hint">
                        {activePointCount} / {columns * rows} active points
                    </p>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Blob shape" icon={Waves}>
                    <div className="field-row">
                        <label className="field-label">
                            Influence radius (× cell size)
                            <DebouncedInput
                                type="number"
                                min={0.1}
                                max={5}
                                step={0.1}
                                value={influenceRadius}
                                onChange={handleFloat(
                                    setInfluenceRadius,
                                    0.1,
                                    5,
                                )}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Base threshold
                            <DebouncedInput
                                type="number"
                                min={0.01}
                                max={2}
                                step={0.01}
                                value={baseThreshold}
                                onChange={handleFloat(
                                    setBaseThreshold,
                                    0.01,
                                    2,
                                )}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Contour levels
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={6}
                                step={1}
                                value={contourLevels}
                                onChange={handleInt(setContourLevels, 1)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Contour step
                            <DebouncedInput
                                type="number"
                                min={0.01}
                                max={1}
                                step={0.01}
                                value={contourStep}
                                onChange={handleFloat(setContourStep, 0.01, 1)}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Appearance" icon={Palette}>
                    <div className="field-row">
                        <label className="field-label">
                            Fill color
                            <div className="control-color-row">
                                <input
                                    type="color"
                                    value={fillColor}
                                    onChange={(e) =>
                                        handleFillColorPicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={fillColor}
                                    onChange={(v) => setFillColor(String(v))}
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke color
                            <div className="control-color-row">
                                <input
                                    type="color"
                                    value={strokeColor}
                                    onChange={(e) =>
                                        handleStrokeColorPicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={strokeColor}
                                    onChange={(v) => setStrokeColor(String(v))}
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={showGridPoints}
                            onChange={setShowGridPoints}
                            label="Show grid points"
                            inline
                        />
                    </div>
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
